// Worked examples. Each returns a real workbook — the same shape that gets
// persisted — so they can be loaded in the app from the Examples menu and
// dumped to `examples/*.json` by `npm run examples`.
//
// They are written against the LIVE project: the numbers only appear once the
// app is open inside Giraffe (or in `?demo`). That is the point — an example
// full of hardcoded numbers teaches nothing about how the sheet is wired.

import type { FormatId, Sheet, Workbook } from '@/model/types';
import { createWorkbook, setCellRaw } from '@/model/workbook';
import { classifyField } from '@/data/classify';
import { parseCellA1 } from '@/model/address';
import { stampSchedule, type ScheduleSpec } from '@/giraffe/stamp';
import { Engine } from '@/engine/engine';
import type { GiraffeData } from '@/data/types';
import { ymdToSerial } from '@/model/dates';
import { UNDERWRITE_EXAMPLES } from './underwrites';

export interface Example {
  id: string;
  name: string;
  summary: string;
  build(): Workbook;
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function put(sheet: Sheet, cells: Record<string, string>): void {
  for (const [addr, raw] of Object.entries(cells)) {
    const rc = parseCellA1(addr);
    if (!rc) throw new Error(`bad address ${addr}`);
    setCellRaw(sheet, rc.row, rc.col, raw);
  }
}

function style(sheet: Sheet, addrs: string[], patch: { bold?: boolean; bg?: string }): void {
  for (const addr of addrs) {
    const rc = parseCellA1(addr)!;
    const key = `${rc.row},${rc.col}`;
    sheet.cells[key] = { ...sheet.cells[key], s: { ...sheet.cells[key]?.s, ...patch } };
  }
}

function format(sheet: Sheet, range: string, fmt: FormatId): void {
  const [from, to] = range.split(':');
  const a = parseCellA1(from!)!;
  const b = parseCellA1(to ?? from!)!;
  for (let r = Math.min(a.row, b.row); r <= Math.max(a.row, b.row); r++) {
    for (let c = Math.min(a.col, b.col); c <= Math.max(a.col, b.col); c++) {
      const key = `${r},${c}`;
      sheet.cells[key] = { ...sheet.cells[key], fmt };
    }
  }
}

function widths(sheet: Sheet, map: Record<number, number>): void {
  Object.assign(sheet.colWidths, map);
}

/**
 * Stamp a schedule into an example.
 *
 * Examples are built without a live project attached, so the stamp needs a data
 * source to resolve `auto` against. `EMPTY_DATA` classifies everything as
 * `rate`, which would emit GI.WEIGHTEDAVG for an area — so the examples pass a
 * classifier that knows the standard fields.
 */
function schedule(wb: Workbook, sheet: Sheet, anchor: { row: number; col: number }, spec: ScheduleSpec): void {
  const engine = new Engine({ workbook: wb, data: () => STANDARD_FIELDS });
  stampSchedule(sheet, engine, anchor, spec, STANDARD_FIELDS);
}

/**
 * A data source that knows only how to classify — enough to stamp against with
 * no project attached. The formulas resolve for real once one is.
 */
const STANDARD_FIELDS: GiraffeData = {
  version: 0,
  live: false,
  blocks: () => ({}),
  rows: () => [],
  fields: () => [],
  classOf: (_source, field) => classifyField(field),
  selectionKeys: () => null,
  siteArea: () => null,
};

// ---------------------------------------------------------------------------
// Areas by usage
// ---------------------------------------------------------------------------

/**
 * The area schedule — the most-asked-for table in the app, and the clearest
 * demonstration of why measure classes exist.
 *
 * Three different KINDS of number sit side by side here:
 *   footprint  `area`        — one level's worth, so it sums
 *   floor area `grossArea`   — `levels` already applied, so it also sums, but
 *                              it is NOT footprint x levels a second time
 *   efficiency `efficiency`  — a rate, so it takes an area-weighted mean and
 *                              summing it would be meaningless
 *
 * The live block below uses `auto` on each, which resolves through the class,
 * so the schedule is right without anyone having to think about it.
 */
const areasByUsage: Example = {
  id: 'areas-by-usage',
  name: 'Areas by usage',
  summary: 'Footprint, gross, net and saleable per usage, with efficiency, FAR, cost and revenue.',
  build() {
    const wb = createWorkbook();
    const sheet = wb.sheets[0]!;
    // Not 'Areas' — that now clashes with the real system Areas sheet (role
    // 'areas', uniqueSheetName would just rename this one on load anyway,
    // but a name that reads as if it WERE the system sheet is confusing on
    // its own).
    sheet.name = 'AreaSch';
    widths(sheet, { 1: 150, 2: 95, 3: 95, 4: 95, 5: 95, 6: 85, 7: 70, 8: 120, 9: 130 });

    put(sheet, {
      A1: 'Areas by usage',

      A3: 'Site area',
      // The PROJECT BOUNDARY, not the sum of section footprints. A plot ratio
      // built on the wrong one is overstated by however much of the site is not
      // built on.
      B3: '=GI.SITEAREA()',
      A4: 'Building footprint',
      B4: '=GI.FOOTPRINT()',
      A5: 'Site cover',
      B5: '=IFERROR(B4/B3,"—")',
      A6: 'Gross floor area',
      B6: '=GI.TOTAL("grossArea")',
      A7: 'Net area',
      B7: '=GI.TOTAL("netArea")',
      A8: 'Saleable area',
      B8: '=GI.TOTAL("saleableArea")',
      A9: 'FAR (plot ratio)',
      B9: '=IFERROR(B6/B3,"—")',
      A10: 'Overall efficiency',
      B10: '=IFERROR(B7/B6,"—")',

      // Cost and revenue are worked out PER GEOMETRY and then added up.
      // Gross area times an average rate is a different number, and wrong
      // wherever the rate varies between sections — which is the normal case.
      A11: 'Construction cost',
      B11: '=GI.SUMEACH("grossArea * hardCost")',
      A12: 'Gross revenue',
      B12: '=GI.SUMEACH("saleableArea * salePrice")',
      A13: 'Margin',
      B13: '=IFERROR((B12-B11)/B12,"—")',

      A15: 'Schedule by usage',
      A16: 'A live block: it refreshes with the model, and gains a row when the project gains a usage.',
    });

    schedule(wb, sheet, { row: 18, col: 1 }, {
      source: 'bakedSections',
      groupBy: 'usage',
      pivotBy: null,
      measures: [
        { field: 'area', agg: 'auto', label: 'Footprint' },
        { field: 'grossArea', agg: 'auto', label: 'Gross' },
        { field: 'netArea', agg: 'auto', label: 'Net' },
        { field: 'saleableArea', agg: 'auto', label: 'Saleable' },
        { field: 'efficiency', agg: 'auto', label: 'Efficiency', fmt: 'percent0' },
        { field: 'hardCost', agg: 'sum', expr: 'grossArea * hardCost', label: 'Cost', fmt: 'currency0' },
        { field: 'salePrice', agg: 'sum', expr: 'saleableArea * salePrice', label: 'Revenue', fmt: 'currency0' },
      ],
      filters: [],
      rows: 10,
      showHeader: true,
      showTotal: true,
    });

    // FAR is each usage's gross area over the SITE — ordinary arithmetic beside
    // the schedule rather than another GI call, because that is what it is.
    put(sheet, { I18: 'FAR' });
    style(sheet, ['I18'], { bold: true });
    for (let r = 19; r <= 28; r++) {
      put(sheet, { [`I${r}`]: `=IF($A${r}="","",IFERROR(C${r}/$B$3,""))` });
    }
    put(sheet, { I29: '=IFERROR(C29/$B$3,"")' });
    format(sheet, 'I19:I29', 'number2');
    style(sheet, ['I29'], { bold: true });

    put(sheet, {
      A31: 'Footprint is one level. Gross already has levels applied — it is not footprint × levels.',
      A32: 'Efficiency is a rate: a floor-area-weighted mean, which is exactly net ÷ gross.',
      A33: 'Cost and revenue loop per geometry — GI.SUMEACH — not area × an average rate.',
    });

    wb.charts['cht-areas'] = {
      id: 'cht-areas',
      sheetId: sheet.id,
      type: 'bar',
      title: 'Revenue and cost by usage',
      labelRange: 'A19:A28',
      dataRange: 'G18:H28',
      seriesInFirstRow: true,
      x: 760,
      y: 24,
      w: 440,
      h: 300,
    };

    style(sheet, ['A1', 'A15', 'A9', 'A10', 'A11', 'A12', 'A13'], { bold: true });
    format(sheet, 'B3:B4', 'number0');
    format(sheet, 'B5', 'percent0');
    format(sheet, 'B6:B8', 'number0');
    format(sheet, 'B9', 'number2');
    format(sheet, 'B10', 'percent0');
    format(sheet, 'B11:B12', 'currency0');
    format(sheet, 'B13', 'percent0');
    return wb;
  },
};

// ---------------------------------------------------------------------------
// Unit mix
// ---------------------------------------------------------------------------

/**
 * Unit counts by type — the question this app gets asked most.
 *
 * The dwelling mix arrives as ONE FIELD PER BED TYPE
 * (`dwelling-1BED-Count`, `dwelling-2BED-Count`, …), not as values of a
 * "bed type" column. So the mix is measures-as-columns, and a cross-tab is the
 * wrong shape for it — that is why the block below lists the mix fields rather
 * than pivoting on one.
 *
 * The field names come from the user's own mix, so the block here names the
 * common ones; open the Giraffe data pane to swap in whatever this project uses
 * (autocomplete inside a GI.* call lists every field it actually has).
 */
const unitMix: Example = {
  id: 'unit-mix',
  name: 'Unit mix',
  summary: 'Unit counts by bed type and usage, with a saleable-area check and a chart-ready table.',
  build() {
    const wb = createWorkbook();
    const sheet = wb.sheets[0]!;
    sheet.name = 'Unit mix';
    widths(sheet, { 1: 170, 2: 90, 3: 90, 4: 90, 5: 100, 6: 110, 7: 110 });

    put(sheet, {
      A1: 'Unit mix',
      A2: 'Counts come straight off the baked sections and follow the model.',

      A4: 'Dwellings, all usages',
      B4: '=GI.TOTAL("dwellingTotalCount")',
      A5: 'Dwellings, residential only',
      B5: '=GI.TOTAL("dwellingTotalCount","usage=Residential")',
      A6: 'Distinct usages',
      B6: '=GI.COUNTUNIQUE("usage")',
      A7: 'Saleable area per dwelling',
      B7: '=IFERROR(GI.TOTAL("saleableArea","usage=Residential")/B5,"—")',

      A9: 'By bed type',
      A10: 'Bed type',
      B10: 'Units',
      C10: 'Share',
      D10: 'Saleable area',

      A15: 'Total',
      B15: '=SUM(B11:B14)',
      C15: '=SUM(C11:C14)',
    });

    // One row per bed type, written out rather than left as a drag for the user.
    const beds = ['1BED', '2BED', '3BED', '4BED'];
    beds.forEach((bed, i) => {
      const r = 11 + i;
      put(sheet, {
        [`A${r}`]: bed,
        [`B${r}`]: `=GI.TOTAL("dwelling-${bed}-Count")`,
        [`C${r}`]: `=IFERROR(B${r}/$B$15,0)`,
        [`D${r}`]: `=IFERROR(B${r}/$B$15*GI.TOTAL("saleableArea","usage=Residential"),0)`,
      });
    });

    schedule(wb, sheet, { row: 18, col: 1 }, {
      source: 'bakedSections',
      groupBy: 'usage',
      pivotBy: null,
      measures: [
        { field: 'dwelling-1BED-Count', agg: 'sum', label: '1 bed' },
        { field: 'dwelling-2BED-Count', agg: 'sum', label: '2 bed' },
        { field: 'dwelling-3BED-Count', agg: 'sum', label: '3 bed' },
        { field: 'dwellingTotalCount', agg: 'sum', label: 'Total units' },
        { field: 'saleableArea', agg: 'sum', label: 'Saleable area' },
      ],
      filters: [],
      rows: 8,
      showHeader: true,
      showTotal: true,});

    put(sheet, { A17: 'Mix by usage — live block, refreshes with the model' });

    wb.charts['cht-mix'] = {
      id: 'cht-mix',
      sheetId: sheet.id,
      type: 'bar',
      title: 'Units by bed type',
      labelRange: 'A11:A14',
      dataRange: 'B10:B14',
      seriesInFirstRow: true,
      x: 640,
      y: 24,
      w: 420,
      h: 260,
    };

    style(sheet, ['A1', 'A4', 'A9', 'A10', 'B10', 'C10', 'D10', 'A15', 'B15', 'C15', 'A17'], { bold: true });
    format(sheet, 'B4:B6', 'number0');
    format(sheet, 'B7', 'number0');
    format(sheet, 'B11:B15', 'number0');
    format(sheet, 'C11:C15', 'percent0');
    format(sheet, 'D11:D14', 'number0');
    return wb;
  },
};

// ---------------------------------------------------------------------------
// Residual appraisal
// ---------------------------------------------------------------------------

/**
 * A one-page residual land value. Revenue is built from a live schedule by
 * usage; costs come off the usage rates. Every input the user is meant to
 * change sits in one block at the top rather than being buried in a formula.
 */
const residual: Example = {
  id: 'residual-appraisal',
  name: 'Residual appraisal',
  summary: 'Schedule by usage, revenue and cost from the model, and the residual land value.',
  build() {
    const wb = createWorkbook();
    const sheet = wb.sheets[0]!;
    sheet.name = 'Appraisal';
    widths(sheet, { 1: 190, 2: 120, 3: 110, 4: 120, 5: 130 });

    put(sheet, {
      A1: 'Residual land value',

      A3: 'Assumptions',
      A4: 'Fees and contingency',
      B4: '15%',
      A5: 'Selling costs',
      B5: '3%',
      A6: 'Developer profit on revenue',
      B6: '20%',
      A7: 'Finance rate (annual)',
      B7: '7.5%',

      A9: 'Schedule by usage',
      A10: 'Usage',
      B10: 'Gross area',
      C10: 'Saleable',
      D10: 'Sale rate',
      E10: 'Revenue',

      A16: 'Total',
      B16: '=SUM(B11:B15)',
      C16: '=SUM(C11:C15)',
      E16: '=SUM(E11:E15)',

      A18: 'Appraisal',
      A19: 'Gross development value',
      B19: '=E16',
      A20: 'Selling costs',
      B20: '=-B19*$B$5',
      A21: 'Construction cost',
      B21: '=-GI.WEIGHTEDAVG("hardCost")*B16',
      A22: 'Fees and contingency',
      B22: '=B21*$B$4',
      A23: 'Developer profit',
      B23: '=-B19*$B$6',
      A24: 'Residual land value',
      B24: '=B19+B20+B21+B22+B23',
      A26: 'Land value per dwelling',
      B26: '=IFERROR(B24/GI.TOTAL("dwellingTotalCount"),"—")',
      A27: '="Land value per "&IF(GI.UNITS()="imperial","ft²","m²")&" of site"',
      B27: '=IFERROR(B24/GI.AREA(),"—")',
    });

    for (let r = 11; r <= 15; r++) {
      put(sheet, {
        [`A${r}`]: `=GI.LIST("usage",ROW()-ROW($A$10))`,
        [`B${r}`]: `=IF(A${r}="","",GI.TOTAL("grossArea","usage="&A${r}))`,
        [`C${r}`]: `=IF(A${r}="","",GI.TOTAL("saleableArea","usage="&A${r}))`,
        [`D${r}`]: `=IF(A${r}="","",GI.WEIGHTEDAVG("salePrice","usage="&A${r}))`,
        [`E${r}`]: `=IF(A${r}="","",C${r}*D${r})`,
      });
    }

    wb.charts['cht-rev'] = {
      id: 'cht-rev',
      sheetId: sheet.id,
      type: 'doughnut',
      title: 'Revenue by usage',
      labelRange: 'A11:A15',
      dataRange: 'E11:E15',
      seriesInFirstRow: false,
      x: 660,
      y: 24,
      w: 400,
      h: 280,
    };

    style(sheet, ['A1', 'A3', 'A9', 'A18', 'A10', 'B10', 'C10', 'D10', 'E10', 'A16', 'A24', 'B24'], { bold: true });
    format(sheet, 'B4:B7', 'percent0');
    format(sheet, 'B11:C16', 'number0');
    format(sheet, 'D11:E16', 'currency0');
    format(sheet, 'B19:B24', 'currency0');
    format(sheet, 'B26:B27', 'currency0');
    return wb;
  },
};

// ---------------------------------------------------------------------------
// Monthly cashflow
// ---------------------------------------------------------------------------

/**
 * An S-curve cashflow over 24 months, with NPV and IRR.
 *
 * The month row is a DATE SERIES: cell B4 holds a real date and the rest are
 * dragged from it, so the whole programme moves by editing one cell. Dates are
 * Excel serials here, which is why EOMONTH and XNPV work on them.
 */
const cashflow: Example = {
  id: 'cashflow',
  name: 'Monthly cashflow',
  summary: 'A 24-month S-curve draw against sales, with NPV and IRR. Drag the first date to move the programme.',
  build() {
    const wb = createWorkbook();
    const sheet = wb.sheets[0]!;
    sheet.name = 'Cashflow';
    widths(sheet, { 1: 190, 2: 100 });

    const months = 24;
    const start = ymdToSerial(new Date().getUTCFullYear() + 1, 1, 1);

    put(sheet, {
      A1: 'Monthly cashflow',
      A2: 'Edit the start date in B4 and drag it across — the programme follows.',

      A3: 'S-curve weight',
      A4: 'Month',
      A5: 'Construction cost',
      A6: 'Sales revenue',
      A7: 'Net',
      A8: 'Cumulative',

      A10: 'Programme',
      A11: 'Build months',
      B11: '18',
      A12: 'First settlement month',
      B12: '13',
      A13: 'Discount rate (annual)',
      B13: '9%',

      A15: 'Total cost',
      B15: '=-GI.WEIGHTEDAVG("hardCost")*GI.TOTAL("grossArea")*1.15',
      A16: 'Total revenue',
      B16: '=GI.TOTAL("saleableArea")*GI.WEIGHTEDAVG("salePrice")',
      A17: 'NPV at discount rate',
      B17: '=NPV($B$13/12, B7:Y7)',
      A18: 'IRR (monthly)',
      B18: '=IFERROR(IRR(B7:Y7),"—")',
      A19: 'IRR (annualised)',
      B19: '=IFERROR(POWER(1+B18,12)-1,"—")',
    });

    // The date row is written as serials with a month format: this is what the
    // fill handle continues, and what EOMONTH/XNPV can read.
    for (let i = 0; i < months; i++) {
      const col = 2 + i;
      const addr = (row: number): string => `${colLabel(col)}${row}`;
      sheet.cells[`4,${col}`] = { v: start + monthOffsetDays(start, i), fmt: 'monthYear' };
      put(sheet, {
        // The S-curve lives in its own row of weights, normalised by their sum,
        // rather than as one unreadable expression per cell. Slow, fast, slow.
        [addr(3)]: `=IF(COLUMN()-1>$B$11,0,SIN(PI()*(COLUMN()-1)/($B$11+1)))`,
        [addr(5)]: `=IFERROR($B$15*${addr(3)}/SUM($B$3:$Y$3),0)`,
        [addr(6)]: `=IF(COLUMN()-1<$B$12,0,$B$16/(${months}-$B$12+1))`,
        [addr(7)]: `=${addr(5)}+${addr(6)}`,
        [addr(8)]: i === 0 ? `=${addr(7)}` : `=${colLabel(col - 1)}8+${addr(7)}`,
      });
    }

    wb.charts['cht-cf'] = {
      id: 'cht-cf',
      sheetId: sheet.id,
      type: 'line',
      title: 'Cumulative position',
      labelRange: 'B4:Y4',
      dataRange: 'B8:Y8',
      seriesInFirstRow: false,
      x: 40,
      y: 480,
      w: 720,
      h: 280,
    };

    style(sheet, ['A1', 'A4', 'A7', 'A8', 'A10', 'A15', 'A16', 'A17', 'A19'], { bold: true });
    format(sheet, 'B3:Y3', 'number2');
    format(sheet, 'B13', 'percent0');
    format(sheet, 'B15:B17', 'currency0');
    format(sheet, 'B18:B19', 'percent1');
    for (let i = 0; i < months; i++) {
      const col = colLabel(2 + i);
      format(sheet, `${col}5:${col}8`, 'currency0');
    }
    return wb;
  },
};

function colLabel(col: number): string {
  let n = col;
  let out = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

/** Days from `start` to the same day, `i` months later. */
function monthOffsetDays(start: number, i: number): number {
  const date = new Date(Date.UTC(1899, 11, 30) + start * 86_400_000);
  const target = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + i, date.getUTCDate()));
  return Math.round((target.getTime() - Date.UTC(1899, 11, 30)) / 86_400_000) - start;
}

export const EXAMPLES: Example[] = [areasByUsage, unitMix, residual, cashflow, ...UNDERWRITE_EXAMPLES];

export function exampleById(id: string): Example | undefined {
  return EXAMPLES.find((e) => e.id === id);
}

